import { ParamMap, Params } from '@angular/router';
import { SortDescriptor } from '@progress/kendo-data-query';
import { IGridState, PageSize } from 'src/app/core/models/base';

/**
 * Helpers to persist Kendo-grid paging and sorting in URL query params.
 *
 * Query params:
 *  - page      1-based page number, omitted when 1
 *  - pageSize  25 | 50 | 100, omitted when 25
 *  - sort/dir  grid field and 'asc' | 'desc', omitted when equal to the default sort
 */

export const DEFAULT_PAGE_SIZE: number = PageSize.Small;
export const ALLOWED_PAGE_SIZES: number[] = [PageSize.Small, PageSize.Medium, PageSize.Large];

export interface IPagingQuery {
  /** 1-based page number */
  pageNumber: number;
  pageSize: number;
  skip: number;
}

export function pageFromSkip(skip: number, take: number): number {
  return take > 0 ? Math.floor(skip / take) + 1 : 1;
}

export function parsePagingQuery(q: ParamMap): IPagingQuery {
  const rawSize = Number(q.get('pageSize'));
  const pageSize = ALLOWED_PAGE_SIZES.includes(rawSize) ? rawSize : DEFAULT_PAGE_SIZE;

  const rawPage = Number(q.get('page'));
  const pageNumber = Number.isInteger(rawPage) && rawPage >= 1 ? rawPage : 1;

  return { pageNumber, pageSize, skip: (pageNumber - 1) * pageSize };
}

/** Builds the paging query params from the grid state, omitting defaults. */
export function buildPagingQueryParams(state: IGridState): Params {
  const qp: Params = {};
  const page = pageFromSkip(state.skip, state.pageSize);
  if (page > 1) qp['page'] = page;
  if (state.pageSize !== DEFAULT_PAGE_SIZE) qp['pageSize'] = state.pageSize;
  return qp;
}

export function parseSortQuery(q: ParamMap, defaultSort: SortDescriptor, allowedFields?: string[]): SortDescriptor[] {
  const field = q.get('sort');
  const dir = q.get('dir');
  const fieldOk = !!field && (!allowedFields || allowedFields.includes(field));
  const dirOk = dir === 'asc' || dir === 'desc';
  return fieldOk && dirOk
    ? [{ field: field!, dir: dir as 'asc' | 'desc' }]
    : [{ ...defaultSort }];
}

/** Builds sort/dir query params, omitting them when unsorted or equal to the default. */
export function buildSortQueryParams(sort: SortDescriptor[], defaultSort: SortDescriptor): Params {
  const s = sort?.[0];
  if (!s?.field || !s.dir) return {};
  if (s.field === defaultSort.field && s.dir === defaultSort.dir) return {};
  return { sort: s.field, dir: s.dir };
}
